import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { skipToken, useQuery } from '@tanstack/react-query';
import { Save, Table } from 'grommet-icons';
import { ApiDataTable } from './ApiDataTable';
import { API_DATA_FILTER_DEFAULTS } from './ColumnsHelper';
import { NoFiltersMessage } from './NoFiltersMessage';
import { ApiError, TListClientsRequest } from '/src/api';
import { ApiDataService } from '/src/api/services/ApiDataService';
import {
  ApiDataFilterQueryState,
  ApiDataTableViewQueryState,
  ApiDataViewType,
  TListApiDataRequest,
} from '/src/api/types/ApiData';
import { Box, BoxProps, Card, CardBody, CardHeader, LoadingSpinner, SearchInput, Select } from '/src/components';
import { useGlobalStore, useUserStore } from '/src/context';
import { useRestorableListState, useSyncDraft } from '/src/hooks/useRestorableListState';
import { toastMessages } from '/src/lib/toast';

const API_DATA_LIST_DEFAULTS: ApiDataTableViewQueryState = {
  page: 1,
  search: '',
  per_page: 30,
  order_by: undefined,
  order_direction: undefined,
  ...API_DATA_FILTER_DEFAULTS,
};

export const TableView: React.FC<TableCardProps> = (props) => {
  /* Props */
  const { view = ApiDataViewType.station, shownColumns, filters, hasAppliedFilters, surfaceId, ...boxProps } = props;

  /* Context */
  const userStore = useUserStore();
  const { handleApiError } = useGlobalStore();
  const { t } = useTranslation();

  /* Restorable state */
  const restorableList = useRestorableListState<ApiDataTableViewQueryState>({
    surfaceId: surfaceId || 'api-data-list',
    defaults: { ...API_DATA_LIST_DEFAULTS, ...filters },
    standalone: true,
  });

  const queryState = restorableList.state;
  const setQueryState = restorableList.setState;

  /* Draft state */
  const [draftSearch, setDraftSearch] = useState(queryState.search);

  /* Build the API request */
  const request: TListApiDataRequest = useMemo(
    () => ({
      ...queryState,
      ...filters,
      view_type: view === ApiDataViewType.session ? 'session' : 'charger',
      page: queryState.page,
      search: queryState.search || undefined,
      per_page: queryState.per_page,
      order_by: queryState.order_by as TListApiDataRequest['order_by'],
      order_direction: queryState.order_direction as TListApiDataRequest['order_direction'],
    }),
    [queryState, filters, view]
  );

  const areFiltersSynchronized = Object.entries(filters).every(([key, value]) => {
    const queryValue = queryState[key as keyof ApiDataFilterQueryState];

    return Array.isArray(value) && Array.isArray(queryValue)
      ? JSON.stringify(value) === JSON.stringify(queryValue)
      : value === queryValue;
  });

  /* React Query */
  const {
    data: queryData,
    isLoading: queryIsLoading,
    isFetching,
    error: queryError,
  } = useQuery({
    queryKey: ['api-data:list', userStore.user?.id, request],
    queryFn: hasAppliedFilters && areFiltersSynchronized ? () => ApiDataService.list(request) : skipToken,
    placeholderData: (prev) => prev,
  });

  const queryApiData = queryData?.data;
  const queryMetadata = queryData?.meta;

  useEffect(() => {
    setQueryState({
      ...filters,
      page: 1,
    });
  }, [filters]);

  /* Show toast on query error */
  useEffect(() => {
    if (queryError) {
      handleApiError(queryError as ApiError, toastMessages.listApiData.error);
    }
  }, [queryError]);

  /* Resolved values */
  const _apiData = queryApiData;
  const _metadata = queryMetadata;
  const _currentPage = queryState.page;
  const _isLoading = queryIsLoading || isFetching;
  const isRefetching = isFetching && !queryIsLoading;

  /* Methods */
  const search = useCallback(
    (query?: string) => {
      setQueryState({ search: query || '', page: 1 });
    },
    [setQueryState]
  );

  const handleSort = useCallback(
    (property: string, direction: 'asc' | 'desc') => {
      setQueryState({ order_by: property, order_direction: direction, page: 1 });
    },
    [setQueryState]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setQueryState({ page });
    },
    [setQueryState]
  );

  const handlePerPage = useCallback(
    (perPage: number) => {
      setQueryState({ per_page: perPage, page: 1 });
    },
    [setQueryState]
  );

  /* Sync page from API response */
  useEffect(() => {
    if (queryMetadata && queryMetadata.current_page !== queryState.page) {
      setQueryState({ page: queryMetadata.current_page });
    }
  }, [queryMetadata?.current_page]);

  /* Sync drafts when queryState changes */
  useSyncDraft(queryState, setDraftSearch, (s) => s.search);

  let titleString = '';
  let icon: ReactNode;
  switch (view) {
    case ApiDataViewType.station:
      titleString = t('apiData:stationTitle');
      icon = <Save size="24px" color="brand" />;
      break;
    case ApiDataViewType.session:
      titleString = t('apiData:sessionTitle');
      icon = <Table size="24px" color="brand" />;
      break;
  }

  /* Render */ return (
    <Card {...boxProps} width={'80%'}>
      <CardHeader title={titleString} icon={icon}>
        <SearchInput
          searchQuery={draftSearch}
          setSearchQuery={setDraftSearch}
          placeholder={t('apiData:apiDataSearch')}
          onSearch={search}
          isSearching={isRefetching}
        />
        <Select
          name="per_page_selector"
          value={queryState.per_page} //handlePerPage(value)
          setValue={(value) => handlePerPage(value)}
          options={[
            { label: '10 ' + t('apiData:perPage'), value: 10 },
            { label: '30 ' + t('apiData:perPage'), value: 30 },
            { label: '50 ' + t('apiData:perPage'), value: 50 },
            { label: '100 ' + t('apiData:perPage'), value: 100 },
          ]}
          label={'per page'}
          hideLabel
          width="150px"
        />
      </CardHeader>
      <CardBody pad="none" gap="none" flex innerBoxProps={{ flex: true }}>
        {(_isLoading || (hasAppliedFilters && !areFiltersSynchronized)) && <LoadingSpinner />}
        {!_isLoading && hasAppliedFilters && areFiltersSynchronized && (
          <Box flex>
            <ApiDataTable
              apiData={_apiData}
              shownColumns={shownColumns}
              onSort={handleSort}
              sortProperty={queryState.order_by as TListClientsRequest['order_by']}
              sortDirection={queryState.order_direction as TListClientsRequest['order_direction']}
              metadata={_metadata}
              currentPage={_currentPage}
              perPage={queryState.per_page}
              onPageChange={handlePageChange}
            />
          </Box>
        )}
        {!hasAppliedFilters && <NoFiltersMessage />}
      </CardBody>
    </Card>
  );
};

export type TableCardProps = BoxProps & {
  view?: ApiDataViewType;
  shownColumns: string[];
  filters: ApiDataFilterQueryState;
  hasAppliedFilters: boolean;
  surfaceId?: string;
};
